import React, { useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import "./MiniSemanaMeals.css";

const endpoint =
  `${API_URL}/meal-plan-ai/generate`;

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const HOY = (new Date().getDay() + 6) % 7;
const MACROS = [
  { key: "proteinas", label: "Proteínas" },
  { key: "carbohidratos", label: "Carbohidratos" },
  { key: "grasas", label: "Grasas" },
];
const SECCIONES = [
  "Verdura", "Fruta", "Carne y pescado", "Huevos y lácteos", "Legumbres",
  "Cereales y pan", "Frutos secos y aceites", "Especias y básicos",
];

const raciones = (n) =>
  `${n.toLocaleString("es-ES")} ${n === 1 ? "ración" : "raciones"}`;

export default function MiniSemanaMeals() {
  const [selected, setSelected] = useState(HOY);
  const [vista, setVista] = useState("plan");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Opción elegida por comida ("día_tipo" -> índice) y platos comidos hoy
  const [elegidas, setElegidas] = useState({});
  const [comidasHoy, setComidasHoy] = useState([]);
  const [tachados, setTachados] = useState({});
  const [userReady, setUserReady] = useState(false);
  const [userData, setUserData] = useState({});

  // Cargar user y token desde Capacitor Storage
  useEffect(() => {
    async function fetchUser() {
      const { user, token } = await getSession();
      setUserData({ ...user, token });
      setUserReady(!!user && !!token);
    }
    fetchUser();
  }, []);

  const userId = userData?.id || userData?._id;
  const peso = userData?.weight;
  const altura = userData?.height;
  const objetivo = userData?.goal;
  const token = userData?.token;

  // Cargar plan alimenticio personalizado y lo ya comido hoy
  useEffect(() => {
    if (!userId || !peso || !altura || !objetivo || !token) return;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchPlan = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(endpoint, { userId, peso, altura, objetivo }, { headers });
        setPlan(res.data.mealPlan);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            "No se pudo cargar el plan alimenticio. Intenta más tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
    axios
      .get(`${API_URL}/foods`, { headers })
      .then((res) => {
        const hoy = new Date().toDateString();
        setComidasHoy(
          res.data.filter((f) => new Date(f.date).toDateString() === hoy).map((f) => f.name)
        );
      })
      .catch(() => {});
    // Solo una vez al inicio cuando tienes todo
    // eslint-disable-next-line
  }, [userId, peso, altura, objetivo, token]);

  const dias = plan?.dias;

  // La opción comida hoy manda sobre la elegida
  // n: posición de la comida en el día (en un plan del entrenador el nombre puede repetirse)
  const elegida = (dia, comida, n) => {
    if (dia === HOY) {
      const comidaHoy = comida.opciones.findIndex((o) => comidasHoy.includes(o.nombre));
      if (comidaHoy >= 0) return comidaHoy;
    }
    return elegidas[`${dia}_${n}`] || 0;
  };

  const handleConsumir = async (opcion) => {
    setComidasHoy((prev) => [...prev, opcion.nombre]);
    try {
      await axios.post(
        `${API_URL}/foods`,
        {
          name: opcion.nombre,
          calories: opcion.calorias,
          protein: opcion.proteinas,
          carbs: opcion.carbohidratos,
          fat: opcion.grasas,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError(
        "Error al registrar la comida: " + (err.response?.data?.message || err.message)
      );
      setComidasHoy((prev) => prev.filter((n) => n !== opcion.nombre));
    }
  };

  const perfilIncompleto = !userReady || !userId || !peso || !altura || !objetivo;
  const dia = dias?.[selected];
  const totalDia = (key) =>
    dia ? dia.reduce((s, c, n) => s + (c.opciones[elegida(selected, c, n)][key] || 0), 0) : 0;

  // Lista de la compra: ingredientes de las opciones elegidas de la semana
  const lista = {};
  if (dias) {
    dias.forEach((d, i) =>
      d.forEach((c, n) =>
        c.opciones[elegida(i, c, n)].ingredientes.forEach((ing) => {
          lista[ing] = (lista[ing] || 0) + 1;
        })
      )
    );
  }

  return (
    <div className="semana">
      <p className="app-sub semana-sub">
        {plan?.asignado
          ? "Tu plan, preparado por tu entrenador. Si una comida tiene varias opciones, elige la que prefieras."
          : "Tu semana de comidas, ajustada a tu objetivo diario. Cada comida tiene dos opciones: elige la que prefieras."}
      </p>

      {plan?.asignado && (
        <section className="app-card semana-asignado">
          <span className="app-etiqueta">De tu entrenador</span>
          <h2>{plan.asignado.nombre}</h2>
          {plan.asignado.indicaciones && <p>{plan.asignado.indicaciones}</p>}
        </section>
      )}

      <div className="semana-vistas" role="tablist" aria-label="Vista">
        {[["plan", "Plan"], ["compra", "Lista de la compra"]].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={vista === key}
            className={"semana-dia" + (vista === key ? " is-active" : "")}
            onClick={() => setVista(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {perfilIncompleto && (
        <div className="app-estado">
          Completa tu perfil (peso, altura y objetivo) para ver tu plan de comidas.
        </div>
      )}
      {loading && <div className="app-estado">Generando tu plan de comidas semanal…</div>}
      {error && <div className="app-estado app-estado-error">{error}</div>}

      {dias && vista === "plan" && (
        <>
          <div className="semana-dias" role="tablist" aria-label="Día de la semana">
            {DIAS.map((d, i) => (
              <button
                key={d}
                role="tab"
                aria-selected={selected === i}
                className={"semana-dia" + (selected === i ? " is-active" : "")}
                onClick={() => setSelected(i)}
              >
                {d}
              </button>
            ))}
          </div>

          <section className="app-panel semana-resumen" aria-label="Resumen del día">
            <div className="semana-resumen-kcal">
              <span>Plan del día</span>
              <strong>
                {totalDia("calorias")}
                <small> / {plan.objetivos.calorias} kcal</small>
              </strong>
            </div>
            <div className="semana-macros">
              {MACROS.map(({ key, label }) => {
                const valor = totalDia(key);
                const meta = plan.objetivos[key];
                return (
                  <div key={key} className="semana-macro">
                    <span>{label}</span>
                    <strong>
                      {valor}
                      <small> / {meta} g</small>
                    </strong>
                    <div className="semana-barra" aria-hidden="true">
                      <i style={{ width: `${Math.min(100, (valor / meta) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="semana-comidas" key={selected}>
            {dia.map((comida, idx) => {
              const o = elegida(selected, comida, idx);
              const opcion = comida.opciones[o];
              const consumida = selected === HOY && comidasHoy.includes(opcion.nombre);
              return (
                <article
                  key={idx}
                  className={"semana-comida" + (consumida ? " is-consumida" : "")}
                  style={{ "--d": `${idx * 45}ms` }}
                >
                  <div className="semana-comida-cabecera">
                    <span className="app-etiqueta">{comida.tipo}</span>
                    {comida.opciones.length > 1 && (
                      <div className="semana-opciones" role="group" aria-label={`Opciones de ${comida.tipo}`}>
                        {comida.opciones.map((_, i) => (
                          <button
                            key={i}
                            aria-pressed={o === i}
                            className={"semana-opcion" + (o === i ? " is-active" : "")}
                            disabled={consumida}
                            onClick={() =>
                              setElegidas((prev) => ({ ...prev, [`${selected}_${idx}`]: i }))
                            }
                          >
                            Opción {i + 1}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <h3>{opcion.nombre}</h3>
                  <div className="semana-comida-meta">
                    <span>{raciones(opcion.raciones)}</span>
                    {opcion.tiempo > 0 && <span>{opcion.tiempo} min</span>}
                    <span className="semana-kcal">{opcion.calorias} kcal</span>
                  </div>
                  <ul className="semana-macros-plato" aria-label="Macronutrientes">
                    <li>P {opcion.proteinas} g</li>
                    <li>C {opcion.carbohidratos} g</li>
                    <li>G {opcion.grasas} g</li>
                  </ul>
                  {opcion.preparacion && <p>{opcion.preparacion}</p>}
                  {opcion.ingredientes?.length > 0 && (
                    <ul className="semana-ingredientes">
                      {opcion.ingredientes.map((ing, j) => (
                        <li key={j}>{ing}</li>
                      ))}
                    </ul>
                  )}
                  {opcion.fuente && (
                    <p className="semana-fuente">
                      Receta adaptada de{" "}
                      <a
                        href={`https://es.wikibooks.org/wiki/${encodeURI(opcion.fuente.replace(/ /g, "_"))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Wikilibros
                      </a>{" "}
                      (<a href="https://creativecommons.org/licenses/by-sa/4.0/deed.es" target="_blank" rel="noopener noreferrer">CC BY-SA</a>)
                    </p>
                  )}
                  {selected === HOY && (
                    <button
                      className={"app-btn app-btn-sm" + (consumida ? " app-btn-quiet" : "")}
                      disabled={consumida}
                      onClick={() => handleConsumir(opcion)}
                    >
                      {consumida ? "Consumida" : "La he comido"}
                    </button>
                  )}
                </article>
              );
            })}
          </div>
          <p className="semana-nota">
            Macros aproximados, calculados a partir de los ingredientes de cada receta.
          </p>
        </>
      )}

      {dias && vista === "compra" && (
        <div className="semana-compra">
          <p className="semana-nota">Para toda la semana, con las opciones que tengas elegidas.</p>
          {SECCIONES.map((seccion) => {
            const items = Object.keys(lista)
              .filter((ing) => (plan.secciones[ing] || "Verdura") === seccion)
              .sort((a, b) => a.localeCompare(b, "es"));
            if (items.length === 0) return null;
            return (
              <section key={seccion} className="app-card semana-seccion">
                <h3>{seccion}</h3>
                <ul>
                  {items.map((ing) => (
                    <li key={ing}>
                      <label className={tachados[ing] ? "is-tachado" : ""}>
                        <input
                          type="checkbox"
                          checked={!!tachados[ing]}
                          onChange={(e) => setTachados((prev) => ({ ...prev, [ing]: e.target.checked }))}
                        />
                        {ing}
                        {lista[ing] > 1 && <small> · en {lista[ing]} comidas</small>}
                      </label>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
